
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopNavigation from '../../components/feature/TopNavigation';
import BottomNavigation from '../../components/feature/BottomNavigation';

interface Order {
  id: string;
  date: string;
  from: string;
  to: string;
  price: number;
  status: 'current' | 'completed' | 'cancelled';
  driverName?: string;
  rating?: number;
  tripType: string;
}

export default function OrdersPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'current' | 'completed' | 'cancelled'>('current');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [message, setMessage] = useState('');

  const orders: Order[] = [
    {
      id: '1',
      date: '2024-01-15',
      from: 'الرياض، حي الملز',
      to: 'الرياض، حي النخيل',
      price: 25,
      status: 'current',
      driverName: 'أحمد محمد',
      tripType: 'طلب عادي'
    },
    {
      id: '2',
      date: '2024-01-14',
      from: 'الرياض، حي الورود',
      to: 'الرياض، مطار الملك خالد',
      price: 45,
      status: 'completed',
      driverName: 'محمد علي',
      rating: 4.8,
      tripType: 'طلب مزايدة'
    },
    {
      id: '3',
      date: '2024-01-13',
      from: 'الرياض، حي السليمانية',
      to: 'الرياض، حي الملقا',
      price: 30,
      status: 'completed',
      driverName: 'سعد أحمد',
      rating: 4.9,
      tripType: 'طلب عادي'
    },
    {
      id: '4',
      date: '2024-01-12',
      from: 'الرياض، حي العليا',
      to: 'الرياض، حي الياسمين',
      price: 20,
      status: 'cancelled',
      tripType: 'طلب عادي'
    }
  ];

  const filteredOrders = orders.filter(order => order.status === activeTab);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'current': return 'text-blue-600 bg-blue-50';
      case 'completed': return 'text-green-600 bg-green-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'current': return 'جارية';
      case 'completed': return 'مكتملة';
      case 'cancelled': return 'ملغية';
      default: return status;
    }
  };

  const handleCall = (order: Order) => {
    // Simulate calling driver
    window.location.href = 'tel:+966501234567';
  };

  const handleMessage = (order: Order) => {
    setSelectedOrder(order);
    setShowMessageModal(true);
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      // Simulate sending message
      setMessage('');
      setShowMessageModal(false);
      setSelectedOrder(null);
      // Show success feedback
      alert(t('messageSent'));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20">
      <TopNavigation title={t('orders')} />
      
      <div className="pt-20 px-4">
        {/* Tabs */}
        <div className="flex bg-[#F5F5F5] rounded-xl p-1 mb-6">
          <button
            onClick={() => setActiveTab('current')}
            className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'current'
                ? 'bg-white text-[#0056A6] shadow-sm'
                : 'text-[#707070]'
            }`}
          >
            {t('current')}
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'completed'
                ? 'bg-white text-[#0056A6] shadow-sm'
                : 'text-[#707070]'
            }`}
          >
            {t('completed')}
          </button>
          <button
            onClick={() => setActiveTab('cancelled')}
            className={`flex-1 py-3 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'cancelled'
                ? 'bg-white text-[#0056A6] shadow-sm'
                : 'text-[#707070]'
            }`}
          >
            {t('cancelled')}
          </button>
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-gray-500 text-lg font-medium">{t('noOrders')}</p>
              <p className="text-gray-400 text-sm mt-1">{t('ordersWillAppear')}</p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 card-hover transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-500">{order.date}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusText(order.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{order.tripType}</p>
                  </div>
                </div>

                {/* Route */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#0056A6] rounded-full mr-3"></div>
                    <p className="text-sm text-gray-700 flex-1">{order.from}</p>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 border-2 border-[#F9A826] rounded-full mr-3"></div>
                    <p className="text-sm text-gray-700 flex-1">{order.to}</p>
                  </div>
                </div>

                {/* Bottom Info */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center">
                    {order.driverName && (
                      <div className="flex items-center mr-4">
                        <i className="ri-user-line text-gray-400 text-sm mr-1"></i>
                        <span className="text-sm text-gray-600">{order.driverName}</span>
                      </div>
                    )}
                    {order.rating && (
                      <div className="flex items-center">
                        <i className="ri-star-fill text-yellow-400 text-sm mr-1"></i>
                        <span className="text-sm text-gray-600">{order.rating}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-lg font-bold text-[#0056A6]">{order.price} ريال</p>
                  </div>
                </div>

                {/* Action Buttons for Current Orders */}
                {order.status === 'current' && (
                  <div className="flex space-x-2 mt-4 pt-3 border-t border-gray-100">
                    <button 
                      onClick={() => handleCall(order)}
                      className="flex-1 bg-[#0056A6] text-white py-2 rounded-lg font-medium btn-hover transition-all duration-200"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      {t('callDriver')}
                    </button>
                    <button 
                      onClick={() => handleMessage(order)}
                      className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-medium btn-hover transition-all duration-200"
                    >
                      <i className="ri-message-line mr-2"></i>
                      {t('messageDriver')}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">{t('messageTitle')}</h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">إلى: {selectedOrder.driverName}</p>
            </div>
            
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('typeMessage')}
              className="w-full h-24 p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-[#0056A6] mb-4"
              maxLength={500}
            />
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowMessageModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!message.trim()}
                className="flex-1 py-3 bg-[#0056A6] text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('send')}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
}
